<template>
    <PanelTransition :name="transitionName">
        <div
            v-if="ui.flashcardModal.opened"
            class="fixed inset-0 z-[60]"
            :class="onlyMobileVisible ? 'flex flex-col justify-end' : ''"
            @click.stop="closeClickingOutsideElement($event)"
        >
            <div
                ref="focusTrapContainer"
                role="dialog"
                aria-modal="true"
                :aria-label="localizations[lang].flashcard.createFromSelection"
                class="w-full bg-theme-bg shadow-lg"
                :class="panelClasses"
                :style="panelStyle"
            >
                <!-- Drag pill for mobile bottom sheet -->
                <button
                    v-if="onlyMobileVisible"
                    :aria-label="localizations[lang].close"
                    class="flex w-full shrink-0 cursor-pointer items-center justify-center border-none bg-transparent pb-0.5 pt-2.5"
                    @click="closeModal"
                >
                    <span class="h-1 w-9 rounded-sm bg-theme-toolbar-icon opacity-40" />
                </button>

                <div class="px-4 pb-1 pt-3">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center">
                            <svg-icon icon="flashcard-24px" class="mr-2 text-theme-icon" width="20px" height="20px" />
                            <h2 class="font-noto-sans my-auto text-left font-medium leading-6 text-theme-text">
                                {{ localizations[lang].flashcard.createFromSelection }}
                            </h2>
                        </div>
                        <BaseButton
                            v-if="!onlyMobileVisible"
                            icon="ic_close_white_24px"
                            :tooltip="localizations[lang].close"
                            class="text-theme-toolbar-icon hover:text-theme-text"
                            @click="closeModal"
                        />
                        <button
                            v-else
                            class="mr-2 flex items-center text-theme-accent hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                            :disabled="!canSave"
                            @click="saveFlashcard"
                        >
                            {{ localizations[lang].flashcard.save }}
                        </button>
                    </div>
                </div>

                <div class="min-h-0 flex-1 overflow-y-auto">
                    <div class="p-4">
                        <div class="flex gap-3">
                            <div class="w-1 shrink-0 rounded" :style="{ backgroundColor: selectedColor }"></div>
                            <div class="min-w-0 flex-1">
                                <div class="relative mb-3">
                                    <label class="mb-1 block text-xs font-semibold text-theme-toolbar-icon">
                                        {{ localizations[lang].flashcard.front }}
                                    </label>
                                    <textarea
                                        ref="frontInput"
                                        v-model="frontText"
                                        class="font-noto-sans w-full resize-none rounded-lg border border-theme-border bg-theme-bg p-2 text-sm text-theme-text placeholder-theme-toolbar-icon outline-none focus:border-theme-accent focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                                        :placeholder="localizations[lang].flashcard.frontPlaceholder"
                                        rows="2"
                                    ></textarea>
                                </div>

                                <div class="relative my-3 flex items-center justify-center">
                                    <div class="absolute inset-x-0 top-1/2 h-px bg-theme-border"></div>
                                    <PillIconButton
                                        v-tooltip.top="localizations[lang].flashcard.swapFrontBack"
                                        icon="swap-vert-24px"
                                        class="relative z-10 bg-theme-bg"
                                        :aria-label="localizations[lang].flashcard.swapFrontBack"
                                        variant="muted"
                                        @click="swapFrontBack"
                                    />
                                </div>

                                <div>
                                    <label class="mb-1 block text-xs font-semibold text-theme-toolbar-icon">
                                        {{ localizations[lang].flashcard.back }}
                                    </label>
                                    <textarea
                                        v-model="backText"
                                        class="font-noto-sans w-full resize-none rounded-lg border border-theme-border bg-theme-bg p-2 text-sm text-theme-text placeholder-theme-toolbar-icon outline-none focus:border-theme-accent focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                                        :placeholder="localizations[lang].flashcard.backPlaceholder"
                                        rows="3"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="border-t border-theme-border p-3 pt-3">
                    <div class="mb-3">
                        <HighlighterColorPicker
                            variant="standard"
                            :selected-slug="selectedColorSlug"
                            @select="handleColorSelect"
                        />
                    </div>

                    <div v-if="!onlyMobileVisible" class="flex w-full space-x-2">
                        <button
                            class="w-1/2 rounded-lg border border-theme-border bg-theme-bg py-2 text-theme-text hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                            @click="closeModal"
                        >
                            {{ localizations[lang].flashcard.cancel }}
                        </button>
                        <button
                            class="w-1/2 rounded-lg bg-theme-accent py-2 text-theme-accent-on hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-accent focus-visible:ring-offset-2"
                            :disabled="!canSave"
                            @click="saveFlashcard"
                        >
                            {{ localizations[lang].flashcard.save }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </PanelTransition>
</template>

<script>
'use strict';

import { mapGetters, mapMutations, mapState } from 'vuex';
import BaseButton from '../Components/buttons/BaseButton.vue';
import PillIconButton from '../Components/PillIconButton.vue';
import HighlighterColorPicker from '../HighlighterColorPicker/HighlighterColorPicker.vue';
import PanelTransition from '../Components/PanelTransition.vue';
import focusTrapMixin from '../../js/mixins/focusTrapMixin';
import { calculateLeftPanelPlacement } from '../../js/utils/calculateSelectionToolPosition';

export default {
    name: 'FlashcardFromSelectionModal',
    components: {
        BaseButton,
        PillIconButton,
        HighlighterColorPicker,
        PanelTransition,
    },
    mixins: [focusTrapMixin],
    data() {
        return {
            frontText: '',
            backText: '',
            selectedColorSlug: '',
            selectedColor: '',
            focusTrapEnabled: false,
            removedSourceHighlight: null,
        };
    },
    computed: {
        ...mapState([
            'ui',
            'localizations',
            'lang',
            'currentHighlight',
            'device',
            'flashcardModel',
            'anonymousSession',
        ]),
        ...mapGetters(['onlyMobileVisible', 'allHighlighters']),
        /**
         * @returns {boolean} Whether the form has enough data to save
         */
        canSave() {
            return (
                this.frontText.trim().length > 0 && this.backText.trim().length > 0 && this.selectedColorSlug.length > 0
            );
        },
        panelStyle() {
            if (this.onlyMobileVisible) {
                return {};
            }

            const panelWidth = 450;

            return {
                ...calculateLeftPanelPlacement({
                    selectionTop: this.ui.selectionTool.selectionTop || 0,
                    selectionLeft: this.ui.selectionTool.selectionLeft || 0,
                    viewportHeight: this.device.height || window.innerHeight,
                    panelWidth,
                }),
                width: `${panelWidth}px`,
                maxHeight: '50vh',
                borderRadius: '12px',
                boxShadow: '0 4px 24px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)',
            };
        },
        panelClasses() {
            if (this.onlyMobileVisible) {
                return 'max-w-full max-h-[85vh] overflow-y-auto flex flex-col rounded-t-xl shadow-[0_-4px_24px_rgba(0,0,0,0.15)]';
            }
            return 'overflow-y-auto max-w-md flex flex-col md:max-h-[50vh]';
        },
        transitionName() {
            if (this.onlyMobileVisible) return 'slide-up';
            return 'slide-left';
        },
    },
    watch: {
        'ui.flashcardModal.opened': {
            immediate: true,
            handler(isOpen) {
                this.focusTrapEnabled = isOpen;
                if (isOpen) {
                    this.pauseKeyboardNavigation('flashcard');
                    this.removedSourceHighlight = null;
                    this.frontText = '';
                    this.backText = this.currentHighlight.totalText || '';
                    this.applyDefaultColor();
                    this.$nextTick(() => {
                        if (this.$refs.frontInput) {
                            this.$refs.frontInput.focus();
                        }
                    });
                } else {
                    this.resumeKeyboardNavigation('flashcard');
                }
            },
        },
    },
    created() {
        this.applyDefaultColor();
    },
    methods: {
        ...mapMutations([
            'closeFlashcardModal',
            'hideSelectionTool',
            'openToast',
            'pauseKeyboardNavigation',
            'resumeKeyboardNavigation',
            'setCurrentHighlight',
        ]),
        onFocusTrapEscape() {
            this.closeModal();
        },
        /**
         * Selects the first available highlighter color as the default.
         */
        applyDefaultColor() {
            const defaultColor = this.allHighlighters[0];
            this.selectedColorSlug = defaultColor?.slug || '';
            this.selectedColor = defaultColor?.color || '';
        },
        /**
         * Handles a color selection from the color picker.
         * @param {{ slug: string, color: string }} selected - The chosen color
         */
        handleColorSelect({ slug, color }) {
            this.selectedColorSlug = slug;
            this.selectedColor = color;
        },
        /**
         * Swaps the front and back text fields.
         */
        swapFrontBack() {
            const tempText = this.frontText;
            this.frontText = this.backText;
            this.backText = tempText;
        },
        /**
         * Converts the source highlight into a flashcard, removing the highlight first
         * so a failed creation never leaves both markers. The highlight's data is captured
         * into the flashcard before removal, so no position/text is lost. If the highlight
         * is gone but the creation fails, the modal stays open to retry from the snapshot.
         */
        async saveFlashcard() {
            if (!this.canSave) return;

            const sourceHighlight = this.removedSourceHighlight || this.currentHighlight;
            const flashcard = {
                ...this.flashcardModel,
                frontText: this.frontText.trim(),
                backText: this.backText.trim(),
                color: this.selectedColor,
                colorSlug: this.selectedColorSlug,
                sourceText: sourceHighlight.totalText || undefined,
                sourceTextLength: sourceHighlight.totalLength || undefined,
            };
            flashcard.applySelectionPosition(sourceHighlight);

            if (!this.removedSourceHighlight) {
                const sourceRemoved = await this.removeSourceHighlight();
                if (!sourceRemoved) {
                    this.openToast({ text: this.localizations[this.lang].flashcard.conversionFailed });
                    this.closeModal();
                    return;
                }
                this.removedSourceHighlight = sourceHighlight;
            }

            flashcard.create();
            const stored = await flashcard.store();
            if (!stored) {
                this.openToast({ text: this.localizations[this.lang].flashcard.createFailed });
                return;
            }

            this.$bus.$emit('flashcardMarkerCreated', flashcard);
            this.openToast({ text: this.localizations[this.lang].flashcard.created });
            this.closeModal();
        },
        /**
         * Removes the source highlight/note once it has become a flashcard.
         * @returns {Promise<boolean>} True if removed or nothing to remove, false if the deletion failed
         */
        async removeSourceHighlight() {
            if (!this.currentHighlight.canBeDeleted()) return true;
            if (!this.anonymousSession) {
                const deleted = await this.currentHighlight.destroy();
                if (!deleted) return false;
            }
            this.$store.commit('removeSelection', { uuid: this.currentHighlight.uuid });
            this.$bus.$emit('selectionDestroyed', this.currentHighlight.getUniqueCssClass());
            this.setCurrentHighlight({});
            return true;
        },
        closeModal() {
            this.focusTrapEnabled = false;
            this.frontText = '';
            this.backText = '';
            this.removedSourceHighlight = null;
            this.closeFlashcardModal();
        },
        closeClickingOutsideElement(event) {
            if (this.$el === event.target) {
                this.closeModal();
            }
        },
    },
};
</script>
